import { useContext, useMemo, useRef } from 'react';
import type { PopoverProps as AriaPopoverProps } from 'react-aria-components';
import {
  OverlayTriggerStateContext,
  Popover as AriaPopover,
} from 'react-aria-components';

import { useStackingZIndex } from '@/contexts/StackingContext';
import { PortalEventBoundary } from '@/lib/base/overlays/portal-event-boundary';
import { useDismissOnInteractOutside } from '@/lib/base/overlays/useDismissOnInteractOutside';

/** Stable no-op for the dismiss hook when no trigger state is in context. */
const NOOP_DISMISS = () => {};

/**
 * An UNSTYLED react-aria Popover with the lib/base dismissal wiring — for
 * lib/base overlays whose chrome doesn't fit `Popover` (forced max-height and
 * `size`) or `Dropdown.Popover` (its own ready marker and width).
 *
 * - Non-modal: react-aria's default modal popover dismisses via a
 *   z-index:auto underlay that paints beneath stacked surfaces (slideouts), so
 *   outside clicks never reach it and the popover sticks open.
 * - Outside-pointer dismissal restored with `useDismissOnInteractOutside`
 *   (react-aria disables it for non-modal popovers — rationale there), driven
 *   by the trigger's `OverlayTriggerStateContext`. Render it INSIDE a
 *   `MenuTrigger` / `DialogTrigger`; without trigger state it never dismisses.
 * - Wrapped in `PortalEventBoundary` so React events inside the portal don't
 *   bubble to the trigger's ancestors.
 *
 * Stacks with `useStackingZIndex()`, exactly as `Popover` and `Dropdown.Popover`
 * do: without it, a table menu rendered inside a stacked surface (a slideout)
 * portals to <body> at the default z-index and paints BENEATH that surface,
 * leaving the menu unreachable.
 *
 * Every prop (placement, className, data-* handles, render-function children)
 * passes straight through; a caller `style` is merged over the stacking z-index
 * so caller styles still win. react-aria types `style` as
 * `CSSProperties | ((values) => CSSProperties | undefined)`, and the function
 * form has to be CALLED — spreading it yields nothing (a function has no own
 * enumerable properties), which silently dropped caller styles. The ref is
 * owned here — a caller ref would silently disable dismissal.
 *
 * Under a `DialogTrigger`, render a react-aria `Dialog` inside it — the
 * trigger's `aria-controls` targets that Dialog's id, and the dismissal hook
 * needs it to recognise the trigger (see the note on `Popover`).
 *
 * Feature code must not import this: use `Popover` or `Dropdown.Popover`.
 */
export function NonModalAriaPopover(props: AriaPopoverProps) {
  const popoverRef = useRef<HTMLElement>(null);
  const triggerState = useContext(OverlayTriggerStateContext);
  const zIndex = useStackingZIndex();
  const { style: callerStyle } = props;
  useDismissOnInteractOutside(
    popoverRef,
    triggerState?.isOpen ?? false,
    triggerState?.close ?? NOOP_DISMISS
  );
  // Merged OVER the stacking z-index so a caller's own style (incl. an explicit
  // zIndex) wins, matching Popover / DropdownPopover. The function form is
  // invoked with react-aria's render-props state rather than spread — spreading
  // a function copies none of its returned styles.
  const mergedStyle = useMemo<AriaPopoverProps['style']>(
    () =>
      typeof callerStyle === 'function'
        ? values => ({ zIndex, ...callerStyle(values) })
        : { zIndex, ...callerStyle },
    [callerStyle, zIndex]
  );
  return (
    // The boundary must wrap the AriaPopover ELEMENT (not its children):
    // react-aria's own Escape/keyboard handling lives on the popover root, and
    // React only skips ancestors of the boundary — see portal-event-boundary.
    <PortalEventBoundary>
      {/* `style` after the spread so the merged value wins over props.style. */}
      <AriaPopover isNonModal {...props} ref={popoverRef} style={mergedStyle} />
    </PortalEventBoundary>
  );
}
